<template>
    <div>
        <!-- start page title area-->
        <div class="page-title-area ptb-100">
            <div class="container">
                <div class="page-title-content">
                    <h1>{{$t('annual_reports')}}</h1>
                </div>
            </div>
            <div class="bg-image">
                <img :src="banner_image" alt="Annual Reports Banner">
            </div>
        </div>
        <!-- end page title area -->

        <!-- start blog section -->
        <section id="blog" class="blog-section ptb-70">
            <div class="container">
                <div class="row">
                    <div v-for="(data, index) in content" :key="index" class="col-6 col-md-3">
                        <aside class="widget-area">
                            <div class="widget brochure">
                                <h3 class="title">{{data.year}}</h3>
                                <p>{{data.description}}</p>
                                <div v-if="data.file_q1" class="content-list">
                                    <a href="#" @click.prevent="downloadFile(data.file_q1, `${data.description}_Q1`)" class="btn-block">
                                        <i class='bx bxs-file-pdf'></i>
                                        {{$t("download_q1")}}
                                    </a>
                                </div>
                                <div v-if="data.file_q2" class="content-list">
                                    <a href="#" @click.prevent="downloadFile(data.file_q2, `${data.description}_Q2`)" class="btn-block">
                                        <i class='bx bxs-file-doc'></i>
                                        {{$t("download_q2")}}
                                    </a>
                                </div>
                                <div v-if="data.file_q3" class="content-list">
                                    <a href="#" @click.prevent="downloadFile(data.file_q3, `${data.description}_Q3`)" class="btn-block">
                                        <i class='bx bxs-file-txt'></i>
                                        {{$t("download_q3")}}
                                    </a>
                                </div>
                                <div v-if="data.file_q4" class="content-list">
                                    <a href="#" @click.prevent="downloadFile(data.file_q4, `${data.description}_Q4`)" class="btn-block">
                                        <i class='bx bxs-file-txt'></i>
                                        {{$t("download_q4")}}
                                    </a>
                                </div>
                                <div v-if="data.file_annual" class="content-list">
                                    <a href="#" @click.prevent="downloadFile(data.file_annual, `${data.description}_Annual`)" class="btn-block">
                                        <i class='bx bxs-file-txt'></i>
                                        {{$t("download_annual")}}
                                    </a>
                                </div>                                 
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </section>
        <!-- end blog section -->
    </div>
</template>

<script>
import axios from "../axios";
import { mapActions } from 'vuex';

export default {
    name: 'AnnualReports',
    
    data() {
        return {
            content: [],
            banner_image: ''
        }
    },

    methods: {
        ...mapActions("navigation", ["setNavLink"]),
        ...mapActions("navigation", ["setNavItem"]),
        
        async downloadFile(url, filename) {
            try {
                // Extract original filename and extension
                const originalFilename = url.split('/').pop().split('?')[0];
                const originalExt = originalFilename.includes('.') 
                    ? originalFilename.split('.').pop() 
                    : this.getFileExtensionFromUrl(url);
                
                // Clean the filename
                const cleanFilename = filename.replace(/[^a-z0-9]/gi, '_').toLowerCase();
                const finalFilename = `${cleanFilename}.${originalExt || 'pdf'}`;
                
                // For mobile devices or when we want to force download
                if (this.isMobile() || !url.match(/\.(pdf|docx?|xlsx?|txt)$/i)) {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    const downloadUrl = window.URL.createObjectURL(blob);
                    
                    const a = document.createElement('a');
                    a.href = downloadUrl;
                    a.download = finalFilename;
                    document.body.appendChild(a);
                    a.click();
                    
                    // Cleanup
                    setTimeout(() => {
                        window.URL.revokeObjectURL(downloadUrl);
                        document.body.removeChild(a);
                    }, 100);
                } else {
                    // For desktop with direct PDF links
                    window.open(url, '_blank');
                }
            } catch (error) {
                console.error('Download error:', error);
                // Fallback to traditional download
                window.location.href = `${process.env.VUE_APP_BASE_URL}download?url=${encodeURIComponent(url)}&label=${encodeURIComponent(filename)}`;
            }
        },
        
        getFileExtensionFromUrl(url) {
            const mimeTypes = {
                'application/pdf': 'pdf',
                'application/msword': 'doc',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
                'text/plain': 'txt',
                'application/vnd.ms-excel': 'xls',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx'
            };
            
            // You might need to adjust this based on your API response
            if (url.includes('.pdf')) return 'pdf';
            if (url.includes('.doc')) return 'doc';
            if (url.includes('.docx')) return 'docx';
            if (url.includes('.txt')) return 'txt';
            if (url.includes('.xls')) return 'xls';
            if (url.includes('.xlsx')) return 'xlsx';
            
            return 'pdf'; // default
        },
        
        isMobile() {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        }
    },

    created() {
        this.setNavLink('investor_relations');
        this.setNavItem('annual_reports');

        axios.get(`annual-reports`)
            .then(response => {
                this.content = response.data.data.content;
                this.banner_image = response.data.data.banner_image;
            })
            .catch(e => {
                console.error('Error loading annual reports:', e);
            });
    }
}
</script>

<style scoped>
/* Add any custom styles here */
.page-title-area {
    position: relative;
    overflow: hidden;
}

.bg-image img {
    width: 100%;
    height: auto;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: -1;
    opacity: 0.3;
}

.widget.brochure {
    padding: 20px;
    margin-bottom: 30px;
    border: 1px solid #eee;
    border-radius: 5px;
    transition: all 0.3s ease;
}

.widget.brochure:hover {
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.content-list a {
    display: block;
    padding: 8px 12px;
    margin: 5px 0;
    background: #f8f9fa;
    border-radius: 4px;
    color: #333;
    text-decoration: none;
    transition: all 0.3s ease;
}

.content-list a:hover {
    background: #e9ecef;
    color: #f6921e;
}

.content-list i {
    margin-right: 8px;
}

@media (max-width: 767px) {
    .col-6 {
        flex: 0 0 100%;
        max-width: 100%;
    }
}
</style>